/**
 * Every account, searchable by handle or wallet.
 *
 * Paged with a cursor rather than an offset: this list only grows, and an
 * offset page ten deep re-scans everything above it on each request.
 */
import { useInfiniteQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'

import { api, type AdminUserRow } from '../lib/api'
import { Empty, ErrorNote, Loading, Panel, cents, percent, shortAddress, timeAgo } from '../components/ui'

const SORTS = [
  { key: 'newest', label: 'Newest' },
  { key: 'active', label: 'Last seen' },
  { key: 'balance', label: 'Balance' },
  { key: 'staked', label: 'Staked' },
] as const

interface Page {
  users: AdminUserRow[]
  nextCursor: string | null
}

export function UsersPage() {
  const [query, setQuery] = useState('')
  const [sort, setSort] = useState<(typeof SORTS)[number]['key']>('newest')

  // Debounced so typing a wallet address does not fire a query per character.
  const [search, setSearch] = useState('')
  useEffect(() => {
    const id = window.setTimeout(() => setSearch(query.trim()), 300)
    return () => window.clearTimeout(id)
  }, [query])

  const { data, isLoading, error, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery({
      queryKey: ['users', search, sort],
      initialPageParam: null as string | null,
      queryFn: ({ pageParam }) => {
        const params = new URLSearchParams({ sort, limit: '50' })
        if (search) params.set('q', search)
        if (pageParam) params.set('cursor', pageParam)
        return api.get<Page>(`/users?${params}`)
      },
      getNextPageParam: (last) => last.nextCursor,
    })

  const rows = data?.pages.flatMap((p) => p.users) ?? []

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <div className="min-w-[260px] flex-1">
          <label htmlFor="user-search" className="eyebrow">
            Search
          </label>
          <input
            id="user-search"
            className="input mt-1.5"
            placeholder="handle or wallet address"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            spellCheck={false}
          />
        </div>
        <div>
          <div className="eyebrow mb-1.5">Sort</div>
          <div className="flex gap-1">
            {SORTS.map((s) => (
              <button
                key={s.key}
                className={`btn btn-sm ${sort === s.key ? 'btn-primary' : 'btn-ghost'}`}
                onClick={() => setSort(s.key)}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {error && <ErrorNote>Could not load users.</ErrorNote>}

      <Panel
        title={`Accounts${rows.length ? ` · ${rows.length}${hasNextPage ? '+' : ''}` : ''}`}
      >
        {isLoading ? (
          <Loading />
        ) : rows.length === 0 ? (
          <Empty>{search ? `Nothing matches “${search}”.` : 'No accounts yet.'}</Empty>
        ) : (
          <div className="overflow-x-auto">
            <table className="grid-table">
              <thead>
                <tr>
                  <th>Handle</th>
                  <th>Wallet</th>
                  <th className="text-right">Balance</th>
                  <th className="text-right">Staked</th>
                  <th className="text-right">Returned</th>
                  <th className="text-right">RTP</th>
                  <th className="text-right">Rips</th>
                  <th className="text-right">Held</th>
                  <th className="text-right">Last seen</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((u) => (
                  <tr key={u.id}>
                    <td>
                      <Link
                        to={`/users/${u.id}`}
                        className="font-semibold hover:underline"
                        style={{ color: 'var(--color-info)' }}
                      >
                        {u.username ?? '—'}
                      </Link>
                      {!u.profilePublic && (
                        <span className="ml-2 text-[0.625rem]" style={{ color: 'var(--color-faint)' }}>
                          private
                        </span>
                      )}
                    </td>
                    <td className="hash">{shortAddress(u.address)}</td>
                    <td className="tnum text-right">{cents(u.balanceCents)}</td>
                    <td className="tnum text-right" style={{ color: 'var(--color-muted)' }}>
                      {cents(u.stakedCents)}
                    </td>
                    <td className="tnum text-right" style={{ color: 'var(--color-muted)' }}>
                      {cents(u.returnedCents)}
                    </td>
                    <td
                      className="tnum text-right"
                      style={{
                        color:
                          u.realisedRtp === null
                            ? 'var(--color-faint)'
                            : u.realisedRtp > 1
                              ? 'var(--color-up)'
                              : undefined,
                      }}
                    >
                      {percent(u.realisedRtp, 0)}
                    </td>
                    <td className="tnum text-right">{u.opens}</td>
                    <td className="tnum text-right" style={{ color: 'var(--color-muted)' }}>
                      {u.positions}
                    </td>
                    <td className="tnum text-right" style={{ color: 'var(--color-faint)' }}>
                      {timeAgo(u.lastSeenAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {hasNextPage && (
          <div className="border-t p-3 text-center">
            <button
              className="btn btn-ghost btn-sm"
              onClick={() => void fetchNextPage()}
              disabled={isFetchingNextPage}
            >
              {isFetchingNextPage ? 'Loading…' : 'Load more'}
            </button>
          </div>
        )}
      </Panel>
    </div>
  )
}
